import { useEffect, useState } from "react";
import { useLocation, useNavigate, useParams } from "react-router-dom";
import {
  AccordionSummary,
  Box,
  Button,
  Checkbox,
  CircularProgress,
  Dialog,
  DialogContent,
  DialogContentText,
  DialogTitle,
  Divider,
  IconButton,
  Skeleton,
  Stack,
  TextField,
  Typography,
  useTheme,
} from "@mui/material";

import { ReactComponent as Signature } from "../../images/signature.svg";
import { tokens } from "../../theme";
import { getTimeRange, toStringDate } from "../../utils/helpers";

import { Accordion as CustomAccordion } from "../Accordion";

import axios from "../../api/axios";
import { useSnackbar } from "notistack";
import userAuth from "../../utils/userAuth";
import RunSummaryAccordions from "../RunSummaryAccordions";
import { ArrowLeft, LibraryAddCheck } from "@mui/icons-material";
import Header from "../Header";
import { useRecoilState } from "recoil";
import { userInformations } from "../../atoms/userAtom";

const RunSignoffPage = (props) => {
  const params = useParams();
  const loc = useLocation();
  const { id } = params;

  const product =
    props.mode === "steps" ? props.product : decodeURIComponent(id);

  const theme = useTheme();
  const colors = tokens(theme.palette.mode);

  const navigate = useNavigate();
  const { enqueueSnackbar } = useSnackbar();

  const dateStart =
    props.mode === "steps"
      ? props.dateStart
      : new URLSearchParams(loc.search).get("dateStart");
  const dateEnd =
    props.mode === "steps"
      ? props.dateEnd
      : new URLSearchParams(loc.search).get("dateEnd");
  const station =
    props.mode === "steps"
      ? props.station
      : new URLSearchParams(loc.search).get("station");
  const type =
    props.mode === "steps"
      ? props.type
      : new URLSearchParams(loc.search).get("type");

  const [forms, setForms] = useState([]);
  const [signedoffForms, setSignedoffForms] = useState([]);
  const [description, setDescription] = useState();

  const [approveAll, setApproveAll] = useState(false);
  const [values, setValues] = useState({});
  const [password, setPassword] = useState("");

  const [loading, setLoading] = useState(true);

  const [formIDList, setFormIDList] = useState([]);

  const [open, setOpen] = useState(false);
  const [clicked, setClicked] = useState(false);

  const [user] = useRecoilState(userInformations);
  const [timeRange, setTimeRange] = useState();
  const initiateChecks = (forms) => {
    const temp = {};
    forms
      .filter((form) => form?.signoffs?.length === 0)
      .forEach((element) => {
        temp[element._id] = true;
      });
    setValues(temp);
  };

  const loadSignoffPage = async () => {
    const res = await axios.post("/runsignoff", {
      station,
      product,
      dateStart,
      dateEnd,
    });
    if (userAuth.control(res)) {
      const forms = res.data.forms.sort(
        (a, b) => new Date(a.createdAt) - new Date(b.createdAt),
      );
      setForms(forms);
      setTimeRange(getTimeRange(forms));
      setDescription(res.data.desc[0]);
      setSignedoffForms(forms.filter((form) => form.signoffs.length > 0));
      initiateChecks(forms);
      setLoading(false);
    } else {
      navigate("/login");
      localStorage.removeItem("token");
      localStorage.removeItem("username");
      enqueueSnackbar("Please sign in again!", {
        variant: "error",
      });
    }
  };

  useEffect(() => {
    loadSignoffPage();
  }, []);

  useEffect(() => {
    const allChecked = Object.values(values).every((value) => value === true);
    setApproveAll(allChecked);
    setFormIDList(Object.keys(values).filter((key) => values[key] === true));
  }, [values]);

  const handleApproveAllChange = (_, checked) => {
    setApproveAll(checked);

    const temp = {};
    for (let key in values) {
      temp[key] = checked;
    }
    setValues(temp);
  };

  const handleChange = (id, value) => {
    setValues((prevState) => ({ ...prevState, [id]: value }));
  };

  const checkPassword = async () => {
    const res = await axios.post("/runsignoff/password", {
      username: user.loginUser,
      password,
    });

    if (userAuth.control(res)) {
      const status = res.data?.status;
      if (status === 1) {
        if (!clicked) {
          setClicked(true);
          handleSignoff();
        }
      } else {
        setPassword("");
        enqueueSnackbar("Your password is incorrect!", {
          variant: "error",
        });
      }
    } else {
      switch (res.response?.status) {
        case 404:
          enqueueSnackbar("Contact to your system administrator!", {
            variant: "error",
          });
          break;
        default:
          enqueueSnackbar("Something went wrong with the server!", {
            variant: "error",
          });
          break;
      }
    }
    setOpen(false);
  };

  const handleSignoff = async () => {
    const res = await axios.post("/signoff", { forms: formIDList });
    if (userAuth.control(res)) {
      if (res.data) {
        enqueueSnackbar("You have successfully signed off the forms!", {
          variant: "success",
        });
        await loadSignoffPage();
        setClicked(false);
      } else {
        switch (res.response?.status) {
          case 404:
            enqueueSnackbar("Station or product is wrong!", {
              variant: "error",
            });
            break;
          case 406:
            enqueueSnackbar(
              "You do not have access to signoff the forms. Please contact system admin.",
              {
                variant: "error",
              },
            );
            break;
          default:
            enqueueSnackbar("Something went wrong with the server!", {
              variant: "error",
            });
            break;
        }
      }
    } else {
      navigate("/login");
      localStorage.removeItem("token");
      localStorage.removeItem("username");
      enqueueSnackbar("Please sign in again!", {
        variant: "error",
      });
    }
  };

  const getSignatureState = (forms) => {
    if (signedoffForms.length === 0) {
      return colors.grey[400];
    } else if (signedoffForms.length === forms.length) {
      return colors.ciboInnerGreen[400];
    }
    return colors.orangeAccent[500];
  };

  return clicked ? (
    <Box m="0 20px" textAlign="center">
      <CircularProgress />
    </Box>
  ) : !loading ? (
    <Box m="0 20px">
      {props.mode !== "steps" ? (
        <Header title="Run Sign Off" mb={-5} />
      ) : undefined}
      <Dialog
        fullWidth={true}
        open={open}
        onClose={() => {
          setOpen(false);
          setPassword("");
        }}
      >
        <DialogTitle>
          <Stack
            direction="row"
            width="100%"
            alignItems="center"
            justifyContent="space-between"
          >
            <Button
              color="error"
              sx={{ fontWeight: 600, fontSize: 18 }}
              onClick={() => {
                setOpen(false);
                setPassword("");
              }}
            >
              Cancel
            </Button>
            <Typography fontWeight={600} fontSize={20}>
              Sign Off Authentication
            </Typography>
            <Button
              color="secondary"
              sx={{ fontWeight: 600, fontSize: 18 }}
              onClick={checkPassword}
            >
              Confirm
            </Button>
          </Stack>
        </DialogTitle>
        <DialogContent>
          <TextField
            InputProps={{ sx: { fontSize: 18, fontWeight: 700 } }}
            InputLabelProps={{ sx: { fontSize: 16, fontWeight: 600 } }}
            fullWidth
            variant="outlined"
            sx={{ my: 1 }}
            disabled
            value={localStorage.getItem("username")}
            label="Username"
          />
          <DialogContentText>
            ENTERING THE PIN NUMBER CONFIRMS THAT THE RESULTS HAVE BEEN REVIEWED
            AND ARE CORRECT.
          </DialogContentText>
          <TextField
            type="password"
            inputProps={{ maxLength: 12 }}
            InputProps={{ sx: { fontSize: 18, fontWeight: 600 } }}
            value={password}
            onChange={(e) => {
              setPassword(e.target.value);
            }}
            InputLabelProps={{
              sx: {
                fontSize: 16,
                fontWeight: 600,
              },
            }}
            fullWidth
            sx={{
              my: 1,
              "& label, label.Mui-focused": { color: colors.yoggieRed[500] },
            }}
            placeholder="Confirm 6 Digit Pin"
            label="Required"
          />
        </DialogContent>
      </Dialog>
      {props.mode === "steps" ? (
        <Stack
          direction="row"
          alignItems="center"
          width="100%"
          justifyContent="space-between"
        >
          <IconButton onClick={props.onBack}>
            <ArrowLeft
              sx={{ fontSize: 40, color: colors.ciboInnerGreen[500] }}
            />
          </IconButton>
          <Typography pl={10} fontWeight={600} fontSize={17} textAlign="center">
            {props.text}
          </Typography>
          <Stack direction="row" justifyContent="right">
            <Button
              onClick={() => {
                navigate(
                  "/runsummary/" +
                    encodeURIComponent(props.product) +
                    "?dateStart=" +
                    props.dateStart +
                    "&dateEnd=" +
                    props.dateEnd +
                    "&type=" +
                    type +
                    "&station=" +
                    props.station,
                );
              }}
              size="large"
              color="secondary"
              sx={{ mb: 1, fontWeight: 500, fontSize: 17 }}
            >
              Summary
            </Button>

            <Button
              onClick={() => {
                props.onChange(formIDList);
              }}
              size="large"
              color="secondary"
              sx={{ mb: 1, fontWeight: 600, fontSize: 17 }}
            >
              {props.isLast ? "Sign Off" : "Continue"}
            </Button>
          </Stack>
        </Stack>
      ) : signedoffForms.length !== forms.length ? (
        <Stack direction="row" width="100%" justifyContent="right">
          <Button
            onClick={() => {
              navigate(
                "/runsummary/" +
                  encodeURIComponent(product) +
                  "?dateStart=" +
                  dateStart +
                  "&dateEnd=" +
                  dateEnd +
                  "&type=" +
                  type +
                  "&station=" +
                  station,
              );
            }}
            size="large"
            color="secondary"
            sx={{ mb: 1, fontWeight: 600, fontSize: 17 }}
          >
            Summary
          </Button>

          <Button
            onClick={() => {
              setOpen(true);
            }}
            size="large"
            color="secondary"
            sx={{ mb: 1, fontWeight: 600, fontSize: 17 }}
          >
            Sign Off
          </Button>
        </Stack>
      ) : undefined}

      <CustomAccordion expanded={true}>
        <AccordionSummary
          sx={{
            background:
              theme.palette.mode === "dark" ? colors.grey[700] : "#f2f0f0",
            fontWeight: 600,
            fontSize: 15,
          }}
        >
          <Stack width="100%">
            <Typography variant="h3" fontWeight={600}>
              {product + " • " + description[product]}
            </Typography>
            <Stack
              direction="row"
              alignItems="center"
              justifyContent="space-between"
              width="100%"
            >
              <Stack direction="row" spacing={1} mt={1}>
                <Signature
                  width={25}
                  style={{ marginRight: 10 }}
                  stroke={getSignatureState(forms)}
                  fill={getSignatureState(forms)}
                  strokeWidth="10px"
                />
                <Typography>
                  {station +
                    " " +
                    type +
                    " • " +
                    toStringDate(timeRange?.start, {
                      month: "short",
                      year: "numeric",
                      day: "numeric",
                      hour: "numeric",
                      minute: "numeric",
                    }) +
                    " - " +
                    toStringDate(timeRange?.end, {
                      month: "short",
                      year: "numeric",
                      day: "numeric",
                      hour: "numeric",
                      minute: "numeric",
                    })}
                </Typography>
              </Stack>
              {signedoffForms.length !== forms.length ? (
                <Stack direction="row" alignItems="center">
                  <div>Approve All</div>
                  <Checkbox
                    checkedIcon={<LibraryAddCheck />}
                    color="secondary"
                    size="large"
                    checked={approveAll}
                    onChange={handleApproveAllChange}
                  />
                </Stack>
              ) : undefined}
            </Stack>
          </Stack>
        </AccordionSummary>
      </CustomAccordion>
      {signedoffForms.length !== forms.length ? (
        <RunSummaryAccordions
          isForm={true}
          values={values}
          onChange={handleChange}
          forms={forms.filter((form) => form?.signoffs?.length === 0)}
        />
      ) : (
        <Stack spacing={1} textAlign="center" width="100%" alignItems="center">
          <Stack direction="row" width="100%" justifyContent="right">
            <Button
              onClick={() => {
                navigate(
                  "/runsummary/" +
                    encodeURIComponent(product) +
                    "?dateStart=" +
                    dateStart +
                    "&dateEnd=" +
                    dateEnd +
                    "&type=" +
                    type +
                    "&station=" +
                    station,
                );
              }}
              size="large"
              color="secondary"
              sx={{ mb: 1, fontWeight: 600, fontSize: 17 }}
            >
              Summary
            </Button>
          </Stack>
          <LibraryAddCheck
            sx={{ fontSize: 150, color: colors.ciboInnerGreen[500] }}
          />
          <Typography>
            You're all set! All completed data sheets have been previously
            signed off.
          </Typography>
        </Stack>
      )}
    </Box>
  ) : (
    <Stack alignItems="center" width="100%">
      <Skeleton width="90%" height="120px" variant="rectangular" />
      <Divider />
      <Skeleton width="90%" height="50px" variant="rectangular" />
    </Stack>
  );
};

export default RunSignoffPage;
